// Tiki props, built procedurally in the classic mid-century style:
// carved tiki posts (Marquesan / Witco pop), glass floats in rope nets, pufferfish lamps,
// tiki mugs, a volcano bowl, bamboo torches, palms with real fronds, and lava rocks.
// Hero props can be swapped for AI-generated GLBs later (see hero.js).
import * as THREE from 'three';
import { PALETTE, pbr, surface, woodSet, bambooSet, lavaSet, thatchSet, glow } from './materials.js';
import { createFire } from './fire.js';
import { heroSlot } from './hero.js';
import * as T from './textures.js';
import { windowVertex, windowFragment } from './doorwindow.js';

const TAU = Math.PI * 2;

function rng(seed) {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

/* ---------------- Carved tiki ---------------- */

// A face drawn as a height map (white = raised, black = cut deep), mid-century pop style:
// crown, heavy brow, big lozenge eyes, broad nose, a huge mouth with tongue, hands on the belly.
// Drawn sharp and blurred once at the end: a canvas filter on every shape costs seconds.
const faceCache = new Map();
function faceHeightMap(style) {
  if (faceCache.has(style)) return faceCache.get(style);
  const W = 256, H = 512;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  g.fillStyle = '#808080';
  g.fillRect(0, 0, W, H);
  const shape = (fill, fn) => { g.fillStyle = fill; g.beginPath(); fn(); g.fill(); };
  const cx = W / 2;
  // crown ridges
  for (let i = 0; i < 3; i++) shape('#b0b0b0', () => g.rect(0, 12 + i * 22, W, 10));
  // brow
  shape('#e0e0e0', () => g.ellipse(cx, 118, 110, 26, 0, 0, TAU));
  // eyes: raised rim, deep socket, raised pupil
  for (const dx of [-52, 52]) {
    shape('#f0f0f0', () => g.ellipse(cx + dx, 160, style === 'ku' ? 38 : 44, style === 'ku' ? 24 : 34, 0, 0, TAU));
    shape('#202020', () => g.ellipse(cx + dx, 160, style === 'ku' ? 30 : 34, style === 'ku' ? 16 : 25, 0, 0, TAU));
    shape('#d0d0d0', () => g.ellipse(cx + dx, 162, 11, 11, 0, 0, TAU));
  }
  // nose
  shape('#f5f5f5', () => { g.moveTo(cx, 150); g.lineTo(cx + 34, 238); g.quadraticCurveTo(cx, 256, cx - 34, 238); g.closePath(); });
  // mouth: raised lips, deep cut, a tongue (Marquesan) or bared teeth (Kū)
  shape('#f0f0f0', () => g.ellipse(cx, 305, 104, 50, 0, 0, TAU));
  shape('#101010', () => g.ellipse(cx, 305, 88, 36, 0, 0, TAU));
  if (style === 'ku') for (let x = -70; x <= 70; x += 20) shape('#e8e8e8', () => g.rect(cx + x - 7, 282, 14, 18));
  else shape('#c8c8c8', () => g.ellipse(cx, 318, 30, 20, 0, 0, TAU));
  // arms and hands on the belly
  shape('#d8d8d8', () => g.ellipse(cx - 70, 420, 34, 16, 0.3, 0, TAU));
  shape('#d8d8d8', () => g.ellipse(cx + 70, 420, 34, 16, -0.3, 0, TAU));
  shape('#b8b8b8', () => g.rect(0, 470, W, 8));
  const out = document.createElement('canvas');
  out.width = W; out.height = H;
  const o = out.getContext('2d', { willReadFrequently: true });
  o.fillStyle = '#808080';
  o.fillRect(0, 0, W, H); // what the blur pulls in at the edges
  o.filter = 'blur(2px)';
  o.drawImage(c, 0, 0);
  const img = o.getImageData(0, 0, W, H);
  faceCache.set(style, img);
  return img;
}

const carvedCache = new Map();

/**
 * A carved tiki: a dense cylinder whose front is displaced by the face height map,
 * stained dark with lighter wire-brushed high points and burnt recesses.
 */
export function carvedTiki({ height = 2.2, radius = 0.32, style = 'marquesan' } = {}) {
  const key = style + height + radius;
  if (!carvedCache.has(key)) {
    const img = faceHeightMap(style);
    const geo = new THREE.CylinderGeometry(radius, radius * 1.08, height, 64, 96, false);
    const pos = geo.attributes.position, uv = geo.attributes.uv, nrm = geo.attributes.normal;
    const colors = new Float32Array(pos.count * 3);
    const dark = new THREE.Color(0x120a05), mid = new THREE.Color(0x5a3620), light = new THREE.Color(0xc08a58);
    const c = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const u = uv.getX(i), v = uv.getY(i);
      // the face wraps half the post (u 0.25..0.75, the -z side); rotated to face +z below
      const fu = (u - 0.25) * 2;
      let h = 0.5;
      if (fu > 0 && fu < 1 && Math.abs(nrm.getY(i)) < 0.5) {
        const px = Math.floor(fu * (img.width - 1)), py = Math.floor((1 - v) * (img.height - 1));
        h = img.data[(py * img.width + px) * 4] / 255;
      }
      const d = (h - 0.5) * radius * 0.95; // carve deep enough to read from across the deck
      if (Math.abs(nrm.getY(i)) < 0.5) pos.setXYZ(i, pos.getX(i) + nrm.getX(i) * d, pos.getY(i), pos.getZ(i) + nrm.getZ(i) * d);
      c.copy(mid).lerp(h < 0.5 ? dark : light, Math.min(1, Math.abs(h - 0.5) * 2.6));
      colors.set([c.r, c.g, c.b], i * 3);
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.rotateY(Math.PI); // face toward +z
    geo.computeVertexNormals();
    geo.translate(0, height / 2, 0);
    carvedCache.set(key, geo);
  }
  const { map, bump } = woodSet(0x7a5238);
  const mesh = new THREE.Mesh(carvedCache.get(key), pbr({ map, bumpMap: bump, bumpScale: 1.2, vertexColors: true, roughness: 0.8 }));
  mesh.castShadow = mesh.receiveShadow = true;
  return mesh;
}

/* ---------------- Glass float in a rope net ---------------- */

const floatGeo = new THREE.SphereGeometry(1, 24, 16);
function netGeometry(r) {
  const pts = [];
  const seg = 24;
  for (let k = 0; k < 6; k++) {                      // meridians
    const a = (k / 6) * TAU;
    for (let i = 0; i < seg; i++) {
      const t0 = (i / seg) * Math.PI, t1 = ((i + 1) / seg) * Math.PI;
      pts.push(Math.sin(t0) * Math.cos(a) * r, Math.cos(t0) * r, Math.sin(t0) * Math.sin(a) * r);
      pts.push(Math.sin(t1) * Math.cos(a) * r, Math.cos(t1) * r, Math.sin(t1) * Math.sin(a) * r);
    }
  }
  for (const lat of [-0.5, 0, 0.5]) {                // parallels
    const y = lat * r, rr = Math.sqrt(r * r - y * y);
    for (let i = 0; i < seg; i++) {
      const a0 = (i / seg) * TAU, a1 = ((i + 1) / seg) * TAU;
      pts.push(Math.cos(a0) * rr, y, Math.sin(a0) * rr, Math.cos(a1) * rr, y, Math.sin(a1) * rr);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  return g;
}

/** A glowing glass fishing float in a rope net, optionally hanging from a cord. */
export function glassFloat({ radius = 0.16, color = PALETTE.aqua, hang = 0, lit = 1.2 } = {}) {
  const group = new THREE.Group();
  const glass = new THREE.Mesh(floatGeo, pbr({ color, emissive: color, emissiveIntensity: lit, roughness: 0.15, metalness: 0, transparent: true, opacity: 0.85 }));
  glass.scale.setScalar(radius);
  const net = new THREE.LineSegments(netGeometry(radius * 1.03), new THREE.LineBasicMaterial({ color: 0x3a2a18 }));
  // glass and net are the stand-in for the Meshy float (hero slot "glass-float": a ball in a rope
  // net with a loop on top, loaded once the planet is up), sized to this one and glowing its colour
  const shell = new THREE.Group().add(glass, net);
  group.add(shell);
  heroSlot('glass-float', shell, { fit: (model) => floatFit(model, radius, color, lit) });
  if (hang > 0) {
    const cord = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, hang, 4), pbr({ color: 0x3a2a18 }));
    cord.position.y = radius + hang / 2;
    group.add(cord);
  }
  return group;
}

// the model's ball is about 0.85 of its height (the rest is the loop on top)
const FLOAT_BALL = 0.85, floatLooks = new Map(), grays = new Map();
/** The texture in greys, brightened, so any colour can tint it (the model's glass is aqua). */
function grayOf(map) {
  if (!grays.has(map.uuid)) {
    const img = map.image, c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    const x = c.getContext('2d');
    x.filter = 'grayscale(1) brightness(1.5)';
    x.drawImage(img, 0, 0);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.flipY = map.flipY;
    grays.set(map.uuid, t);
  }
  return grays.get(map.uuid);
}
function floatFit(model, radius, color, lit) {
  const h = model.userData.spec.height, k = (2 * radius) / (h * FLOAT_BALL);
  model.scale.setScalar(k);
  model.position.y = -radius; // the ball's middle where the stand-in's was
  const key = `${color}|${lit}`;
  model.traverse((o) => {
    if (!o.isMesh) return;
    // one material per colour: the glass glows its own colour, lit from inside like the bar's lamps
    if (!floatLooks.has(key + o.material.uuid)) {
      const m = o.material.clone();
      m.color.set(color);
      if (m.map) { m.map = grayOf(m.map); m.emissive.set(color); m.emissiveMap = m.map; m.emissiveIntensity = lit * 0.5; }
      floatLooks.set(key + o.material.uuid, m);
    }
    o.material = floatLooks.get(key + o.material.uuid);
  });
}

/* ---------------- Pufferfish lamp ---------------- */

let spikeGeo;
/** A spiky pufferfish lamp with a warm glow inside and a real light. */
export function pufferLamp({ radius = 0.22, hang = 0.6, light = 2.4 } = {}) {
  const group = new THREE.Group();
  const shell = new THREE.Mesh(new THREE.SphereGeometry(radius, 20, 14), pbr({ color: 0xe9c48a, emissive: PALETTE.amber, emissiveIntensity: 2.6, roughness: 0.6 }));
  group.add(shell);
  if (!spikeGeo) spikeGeo = new THREE.ConeGeometry(0.012, 0.07, 4).translate(0, 0.035, 0);
  const n = 70;
  const spikes = new THREE.InstancedMesh(spikeGeo, pbr({ color: 0x8a6a3c, roughness: 0.7 }), n);
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2, rr = Math.sqrt(1 - y * y), a = i * 2.39996;
    const dir = new THREE.Vector3(Math.cos(a) * rr, y, Math.sin(a) * rr);
    p.copy(dir).multiplyScalar(radius * 0.96);
    q.setFromUnitVectors(up, dir);
    spikes.setMatrixAt(i, m.compose(p, q, new THREE.Vector3(1, 1, 1)));
  }
  group.add(spikes);
  // fins and tail so it reads as a fish, not a mine
  const fin = pbr({ color: 0xb8864f, roughness: 0.7, side: THREE.DoubleSide });
  const tail = new THREE.Mesh(new THREE.ConeGeometry(radius * 0.45, radius * 0.7, 4), fin);
  tail.rotation.z = Math.PI / 2;
  tail.position.x = -radius * 1.15;
  group.add(tail);
  for (const s of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(radius * 0.13, 10, 8), pbr({ color: 0x111111, roughness: 0.2 }));
    eye.position.set(radius * 0.72, radius * 0.25, s * radius * 0.55);
    group.add(eye);
  }
  const cord = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, hang, 4), pbr({ color: 0x2a1c10 }));
  cord.position.y = radius + hang / 2;
  group.add(cord);
  const pl = new THREE.PointLight(PALETTE.amber, light, 5.5, 1.6);
  group.add(pl);
  return group;
}

/* ---------------- Tiki mug ---------------- */

const mugProfiles = {
  // classic tall tiki mug, slightly waisted
  tall: [[0, 0], [0.07, 0], [0.075, 0.02], [0.068, 0.08], [0.072, 0.15], [0.07, 0.17], [0.064, 0.17]],
  // squat moai / Easter Island mug
  moai: [[0, 0], [0.06, 0], [0.065, 0.03], [0.06, 0.14], [0.05, 0.19], [0.046, 0.19]],
};
/** A glazed tiki mug with a pressed face, plus straw and garnish. */
export function tikiMug({ glaze = PALETTE.teal, shape = 'tall', garnish = true } = {}) {
  const group = new THREE.Group();
  const prof = mugProfiles[shape].map(([x, y]) => new THREE.Vector2(x, y));
  const body = new THREE.Mesh(new THREE.LatheGeometry(prof, 24), pbr({ color: glaze, roughness: 0.25, metalness: 0.05 }));
  body.castShadow = true;
  group.add(body);
  // pressed face: brow, eyes and mouth as darker inset shapes
  const face = pbr({ color: new THREE.Color(glaze).multiplyScalar(0.45), roughness: 0.4 });
  const at = (w, h, y) => { const s = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.012), face); s.position.set(0, y, 0.068); group.add(s); return s; };
  at(0.09, 0.012, 0.125);
  for (const x of [-0.025, 0.025]) { const e = at(0.028, 0.02, 0.105); e.position.x = x; }
  at(0.07, 0.022, 0.06);
  if (garnish) {
    const straw = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.004, 0.16, 5), pbr({ color: PALETTE.hibiscus, roughness: 0.5 }));
    straw.position.set(0.02, 0.2, 0);
    straw.rotation.z = -0.25;
    group.add(straw);
    const mint = new THREE.Mesh(new THREE.IcosahedronGeometry(0.035, 0), pbr({ color: 0x2f7a3e, roughness: 0.8 }));
    mint.position.set(-0.02, 0.18, 0);
    mint.scale.set(1, 0.6, 1);
    group.add(mint);
  }
  return group;
}

/* ---------------- Volcano bowl ---------------- */

/** Wide communal bowl with a centre cone of flaming rum. */
export function volcanoBowl() {
  const group = new THREE.Group();
  const prof = [[0, 0], [0.09, 0], [0.12, 0.02], [0.17, 0.07], [0.175, 0.08], [0.16, 0.08], [0.1, 0.035], [0.05, 0.035], [0.04, 0.09], [0.028, 0.1], [0, 0.1]]
    .map(([x, y]) => new THREE.Vector2(x, y));
  const bowl = new THREE.Mesh(new THREE.LatheGeometry(prof, 32), pbr({ color: 0x2b1a12, roughness: 0.3 }));
  bowl.castShadow = true;
  group.add(bowl);
  const drink = new THREE.Mesh(new THREE.RingGeometry(0.05, 0.155, 32).rotateX(-Math.PI / 2), pbr({ color: 0xc8551f, emissive: 0x6a2008, emissiveIntensity: 0.8, roughness: 0.1 }));
  drink.position.y = 0.065;
  group.add(drink);
  const fire = createFire({ width: 0.09, height: 0.16, light: 1.2, distance: 3 });
  fire.group.position.y = 0.1;
  group.add(fire.group);
  group.userData.update = fire.update;
  return group;
}

/* ---------------- Torch ---------------- */

/** A bamboo tiki torch with a real flame. */
export function tikiTorch({ height = 2.2, light = 2.2, shadow = false } = {}) {
  const group = new THREE.Group();
  const bamboo = surface(bambooSet(), { repeat: [1, height / 1.2] });
  // a tapering bamboo pole with raised nodes
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.042, 0.058, height, 10), bamboo);
  pole.position.y = height / 2;
  pole.castShadow = true;
  group.add(pole);
  const node = new THREE.TorusGeometry(1, 0.18, 6, 14).rotateX(Math.PI / 2);
  for (let i = 1; i < 5; i++) {
    const y = (i / 5) * height * 0.92, r = 0.058 - (y / height) * 0.016;
    const n = new THREE.Mesh(node, bamboo);
    n.scale.setScalar(r * 1.02);
    n.position.y = y;
    group.add(n);
  }
  // rope lashing below the cup: a tight helix
  const lash = [];
  for (let i = 0; i <= 90; i++) { const k = i / 90, a = k * TAU * 9; lash.push(new THREE.Vector3(Math.cos(a) * 0.05, height - 0.2 + k * 0.14, Math.sin(a) * 0.05)); }
  const rope = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(lash), 180, 0.009, 5), pbr({ color: PALETTE.thatch, roughness: 1 }));
  group.add(rope);
  // a cup of split bamboo slats flaring out, round a dark metal fuel canister and its wick
  const slat = new THREE.BoxGeometry(0.03, 0.3, 0.01).translate(0, 0.15, 0);
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * TAU;
    const m = new THREE.Mesh(slat, bamboo);
    m.position.set(Math.cos(a) * 0.075, height - 0.1, Math.sin(a) * 0.075);
    m.rotation.set(0, -a + Math.PI / 2, 0);
    m.rotateX(0.3); // flaring outward
    m.castShadow = true;
    group.add(m);
  }
  const can = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.05, 0.14, 16), pbr({ color: PALETTE.lava, metalness: 0.6, roughness: 0.4 }));
  can.position.y = height + 0.06;
  group.add(can);
  // all of it so far is the stand-in for the Meshy torch (hero slot "tiki-torch", its canister's top
  // where this one's is); the wick and the fire stay
  const stand = new THREE.Group();
  stand.add(...group.children);
  group.add(stand);
  heroSlot('tiki-torch', stand, { fit: (model) => model.scale.setScalar((height + 0.13) / model.userData.spec.height) });
  const wick = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.014, 0.05, 8), glow(PALETTE.coral, 1.5));
  wick.position.y = height + 0.13;
  group.add(wick);
  const fire = createFire({ width: 0.28, height: 0.62, light, distance: 7, shadow });
  fire.group.position.y = height + 0.13;
  group.add(fire.group);
  group.userData.update = fire.update;
  return group;
}

/* ---------------- Palm ---------------- */

let frondTex;
function frondTexture() {
  if (frondTex) return frondTex;
  const c = document.createElement('canvas');
  c.width = 128; c.height = 512;
  const g = c.getContext('2d');
  g.clearRect(0, 0, 128, 512);
  // leaflets: broad tapered blades swept back from the midrib, overlapping like a real frond
  const r = rng(11);
  for (let y = 505; y > 12; y -= 15) {
    const len = 58 * Math.sin((y / 512) * Math.PI * 0.95) + 8;
    for (const s of [-1, 1]) {
      const shade = 70 + r() * 55;
      g.fillStyle = `rgb(${34 + shade * 0.25}, ${shade}, ${40 + shade * 0.2})`;
      g.beginPath();
      g.moveTo(64, y);
      g.quadraticCurveTo(64 + s * len * 0.55, y - 4, 64 + s * len, y - 30);
      g.quadraticCurveTo(64 + s * len * 0.5, y - 16, 64, y - 12);
      g.closePath();
      g.fill();
    }
  }
  g.strokeStyle = '#7d7a3a';
  g.lineWidth = 4;
  g.beginPath(); g.moveTo(64, 512); g.lineTo(64, 4); g.stroke();
  frondTex = new THREE.CanvasTexture(c);
  frondTex.colorSpace = THREE.SRGBColorSpace;
  return frondTex;
}

/** A coconut palm: curved ringed trunk, drooping textured fronds, coconuts. */
export function palm({ height = 5.5, lean = 0.35, seed = 1 } = {}) {
  const r = rng(seed);
  const group = new THREE.Group();
  const pts = [];
  for (let i = 0; i <= 8; i++) {
    const k = i / 8;
    pts.push(new THREE.Vector3(Math.sin(k * 1.4) * lean * height * 0.35, k * height, 0));
  }
  const curve = new THREE.CatmullRomCurve3(pts);
  const trunkGeo = new THREE.TubeGeometry(curve, Math.round(height * 16), 0.15, 12, false);
  // taper toward the top, and ring it: each old leaf scar is a ridge you can see in silhouette
  const p = trunkGeo.attributes.position, v = new THREE.Vector3();
  for (let i = 0; i < p.count; i++) {
    const y = p.getY(i) / height;
    const c = curve.getPoint(Math.min(1, Math.max(0, y)));
    const ring = 1 + 0.07 * Math.pow(Math.abs(Math.sin(p.getY(i) * 11)), 6) - 0.03;
    v.fromBufferAttribute(p, i).sub(c).multiplyScalar((1.15 - y * 0.45) * ring);
    p.setXYZ(i, c.x + v.x, p.getY(i), c.z + v.z);
  }
  trunkGeo.computeVertexNormals();
  const ring = document.createElement('canvas');
  ring.width = 64; ring.height = 64;
  const rg = ring.getContext('2d');
  const bark = new THREE.Color(PALETTE.wood).lerp(new THREE.Color(PALETTE.thatch), 0.35);
  rg.fillStyle = '#' + bark.getHexString(); rg.fillRect(0, 0, 64, 64);
  rg.fillStyle = '#' + new THREE.Color(PALETTE.stain).getHexString(); rg.fillRect(0, 0, 64, 12);
  for (let i = 0; i < 40; i++) { rg.fillStyle = `rgba(0,0,0,${0.08 + r() * 0.1})`; rg.fillRect(r() * 64, 12 + r() * 50, 1 + r() * 2, 4 + r() * 10); } // fibres
  const ringTex = new THREE.CanvasTexture(ring);
  ringTex.colorSpace = THREE.SRGBColorSpace;
  ringTex.wrapS = ringTex.wrapT = THREE.RepeatWrapping;
  ringTex.repeat.set(2, height * 3.5);
  const trunk = new THREE.Mesh(trunkGeo, pbr({ map: ringTex, bumpMap: ringTex, bumpScale: 2, roughness: 0.9 }));
  trunk.castShadow = true;
  group.add(trunk);

  const top = curve.getPoint(1);
  const crown = new THREE.Group();
  crown.position.copy(top);
  group.add(crown);
  const frondGeo = new THREE.PlaneGeometry(0.9, 3.2, 1, 10);
  frondGeo.translate(0, 1.6, 0);
  // droop: bend the frond down along its length
  const fp = frondGeo.attributes.position;
  for (let i = 0; i < fp.count; i++) {
    const y = fp.getY(i);
    fp.setZ(i, -Math.pow(y / 3.2, 2) * 1.4);
  }
  frondGeo.computeVertexNormals();
  const frondMat = pbr({ map: frondTexture(), alphaTest: 0.45, side: THREE.DoubleSide, roughness: 0.7 });
  const count = 10;
  for (let i = 0; i < count; i++) {
    const f = new THREE.Mesh(frondGeo, frondMat);
    f.rotation.set(-1.0 - r() * 0.35, (i / count) * TAU + r() * 0.3, 0, 'YXZ');
    f.castShadow = true;
    crown.add(f);
  }
  // two dead fronds hanging straight down under the crown, brown and ragged
  const deadMat = pbr({ map: frondTexture(), alphaTest: 0.45, side: THREE.DoubleSide, roughness: 0.9, color: PALETTE.thatch });
  for (let i = 0; i < 2; i++) {
    const f = new THREE.Mesh(frondGeo, deadMat);
    f.scale.setScalar(0.75);
    f.rotation.set(-2.7 + r() * 0.2, i * 2.6 + r(), 0, 'YXZ');
    crown.add(f);
  }
  for (let i = 0; i < 3; i++) {
    const nut = new THREE.Mesh(new THREE.SphereGeometry(0.1, 10, 8), pbr({ color: PALETTE.stain, roughness: 0.8 }));
    nut.position.set(Math.cos(i * 2.1) * 0.14, -0.15, Math.sin(i * 2.1) * 0.14);
    crown.add(nut);
  }
  group.userData.crown = crown;
  return group;
}

/** A fallen coconut, husk and all: a lumpy brown egg with fibres, about 22 cm long. Loose on the
 *  ground for the physics (physics.js rolls it as a ball of `userData.radius`). */
let nutGeo, nutMat;
export function coconut(seed = 1) {
  if (!nutGeo) {
    nutGeo = new THREE.SphereGeometry(0.1, 24, 16);
    const p = nutGeo.attributes.position, v = new THREE.Vector3();
    for (let i = 0; i < p.count; i++) {
      v.fromBufferAttribute(p, i);
      const k = 1 + 0.05 * Math.sin(v.x * 60) * Math.sin(v.z * 55) + 0.04 * Math.sin(Math.atan2(v.z, v.x) * 3); // three ridges
      p.setXYZ(i, v.x * k, v.y * k * 1.12, v.z * k);
    }
    nutGeo.computeVertexNormals();
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const g = c.getContext('2d'), r = rng(77);
    const husk = new THREE.Color(PALETTE.stain).lerp(new THREE.Color(PALETTE.wood), 0.4);
    g.fillStyle = '#' + husk.getHexString(); g.fillRect(0, 0, 64, 64);
    for (let i = 0; i < 90; i++) { g.fillStyle = r() < 0.5 ? `rgba(0,0,0,${0.1 + r() * 0.15})` : `rgba(230,190,140,${0.06 + r() * 0.08})`; g.fillRect(r() * 64, r() * 64, 1, 5 + r() * 12); } // fibres
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    nutMat = pbr({ map: tex, bumpMap: tex, bumpScale: 1.5, roughness: 0.9 });
  }
  const m = new THREE.Mesh(nutGeo, nutMat);
  m.rotation.set(seed * 1.3, seed * 2.1, seed * 0.7);
  m.castShadow = true;
  m.userData.radius = 0.105;
  m.userData.label = 'Coconut';
  return m;
}

/* ---------------- Lava rock ---------------- */

export function lavaRock({ size = 0.8, seed = 1 } = {}) {
  const r = rng(seed);
  const geo = new THREE.IcosahedronGeometry(size, 2);
  const p = geo.attributes.position, v = new THREE.Vector3();
  const offsets = new Map();
  for (let i = 0; i < p.count; i++) {
    v.fromBufferAttribute(p, i);
    const key = v.toArray().map((n) => n.toFixed(3)).join();
    if (!offsets.has(key)) offsets.set(key, 0.75 + r() * 0.45);
    v.multiplyScalar(offsets.get(key));
    v.y *= 0.65;
    p.setXYZ(i, v.x, v.y, v.z);
  }
  geo.computeVertexNormals();
  const m = new THREE.Mesh(geo, surface(lavaSet(), { color: 0x9a8a80, bumpScale: 3, roughness: 0.95 }));
  m.castShadow = m.receiveShadow = true;
  return m;
}

export { glow };

/* ---------------- A bowl of petunias ---------------- */

/**
 * A terracotta pot of petunias that has just fallen out of the sky (The Hitchhiker's Guide to
 * the Galaxy): a shallow dent in the ground, the pot on its side with a bite out of it, its
 * shards round it, the soil spilled out of its mouth and the petunias in it, still flowering.
 * About 90 cm across, a few hundred triangles; nothing moves, so batch.js merges it.
 */
export function petunias(seed = 42) {
  const r = rng(seed);
  const g = new THREE.Group();
  const clay = pbr({ color: 0xa4512f, roughness: 0.92, side: THREE.DoubleSide });
  const dirt = pbr({ color: PALETTE.soil, roughness: 1 });
  const stem = pbr({ color: 0x3d6b2f, roughness: 0.8 });
  const blooms = [pbr({ color: 0x8a3bb0, roughness: 0.6, side: THREE.DoubleSide }), pbr({ color: 0xc8357f, roughness: 0.6, side: THREE.DoubleSide })];
  const add = (m, [x, y, z], [rx = 0, ry = 0, rz = 0] = []) => { m.position.set(x, y, z); m.rotation.set(rx, ry, rz); m.castShadow = m.receiveShadow = true; g.add(m); return m; };

  // the dent it made, ragged at the edge, and clods of earth thrown out round it
  const dent = new THREE.CircleGeometry(0.34, 14).rotateX(-Math.PI / 2);
  const dp = dent.attributes.position;
  for (let i = 1; i < dp.count; i++) { const k = 0.8 + r() * 0.35; dp.setXYZ(i, dp.getX(i) * k, 0, dp.getZ(i) * k); }
  if (dp.count > 2) dp.setXYZ(dp.count - 1, dp.getX(1), 0, dp.getZ(1)); // close the edge
  add(new THREE.Mesh(dent, dirt), [0, 0.012, 0]).castShadow = false;
  const clod = new THREE.IcosahedronGeometry(0.035, 0).scale(1, 0.55, 1);
  for (let i = 0; i < 14; i++) {
    const a = r() * TAU, d = 0.3 + r() * 0.22;
    add(new THREE.Mesh(clod, dirt), [Math.cos(a) * d, 0.008, Math.sin(a) * d], [r(), r() * TAU, r()]).scale.setScalar(0.6 + r() * 0.9);
  }

  // the pot: a lathe-turned flowerpot with its rolled rim, on its side, the top third of it gone
  const profile = [[0, 0], [0.07, 0], [0.075, 0.01], [0.11, 0.19], [0.125, 0.195], [0.13, 0.235], [0.118, 0.236], [0.106, 0.2], [0.064, 0.012], [0, 0.012]].map(([x, y]) => new THREE.Vector2(x, y));
  const pot = add(new THREE.Mesh(new THREE.LatheGeometry(profile, 14, Math.PI / 2 - TAU * 0.85, TAU * 0.7), clay), [0.06, 0.095, 0.02], [0.4, 0.3, Math.PI / 2 + 0.17]);
  pot.rotation.order = 'ZYX';
  // the rest of it, in pieces: curved shards of the wall lying where they fell
  const wall = profile.slice(2, 5);
  for (let i = 0; i < 5; i++) {
    const a = r() * TAU, d = 0.2 + r() * 0.25;
    const piece = new THREE.LatheGeometry(wall, 3, 0, 0.5 + r() * 0.5).translate(-0.1, -0.1, 0);
    add(new THREE.Mesh(piece, clay), [Math.cos(a) * d, 0.02, Math.sin(a) * d], [Math.PI / 2 + (r() - 0.5) * 0.5, r() * TAU, (r() - 0.5) * 0.6]).scale.setScalar(0.5 + r() * 0.4);
  }

  // the soil, spilled out of the pot's mouth, and the petunias still in it
  const lump = new THREE.IcosahedronGeometry(0.1, 1).scale(1, 0.4, 1);
  for (const [x, z, s] of [[-0.2, 0.03, 1.1], [-0.32, -0.05, 0.75], [-0.12, -0.1, 0.6]]) add(new THREE.Mesh(lump, dirt), [x, 0.025 * s, z], [0, r() * TAU, 0]).scale.setScalar(s);
  // a petunia: a five-sided trumpet, the five lobes it has
  const trumpet = new THREE.LatheGeometry([[0.003, 0], [0.007, 0.022], [0.018, 0.036], [0.034, 0.043], [0.04, 0.041]].map(([x, y]) => new THREE.Vector2(x, y)), 5);
  const stalk = new THREE.CylinderGeometry(0.003, 0.004, 1, 4).translate(0, 0.5, 0);
  const leaf = new THREE.SphereGeometry(0.03, 6, 4).scale(1, 0.18, 0.5);
  for (let i = 0; i < 8; i++) {
    const x = -0.12 - r() * 0.28, z = (r() - 0.5) * 0.3, h = 0.05 + r() * 0.09;
    const lean = [(r() - 0.5) * 1.1, 0, (r() - 0.5) * 1.1];
    const s = add(new THREE.Mesh(stalk, stem), [x, 0.02, z], lean);
    s.scale.y = h;
    const top = new THREE.Vector3(0, h, 0).applyEuler(s.rotation).add(s.position);
    add(new THREE.Mesh(trumpet, blooms[i % 2]), top.toArray(), [lean[0] * 1.3, r() * TAU, lean[2] * 1.3]);
    for (let k = 0; k < 2; k++) add(new THREE.Mesh(leaf, stem), [x + (r() - 0.5) * 0.06, 0.03 + r() * 0.03, z + (r() - 0.5) * 0.06], [(r() - 0.5) * 0.6, r() * TAU, (r() - 0.5) * 0.6]);
  }
  return g;
}

/* ---------------- Moai head ---------------- */

/** A weathered moai: long head, heavy brow, long nose, tight lips, carved from lava rock. */
export function moai({ height = 2.4 } = {}) {
  const group = new THREE.Group();
  const stone = surface(lavaSet(), { color: 0x8a7f78, bumpScale: 2.5, roughness: 0.95 });
  const h = height;
  // square-ish head: a 4-sided cylinder turned 45°, slightly tapered
  const head = new THREE.Mesh(new THREE.CylinderGeometry(h * 0.21, h * 0.25, h, 4, 1), stone);
  head.rotation.y = Math.PI / 4;
  head.scale.set(1, 1, 0.8);
  head.position.y = h / 2;
  group.add(head);
  const part = (w, hh, d, x, y, z, rx = 0) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, hh, d), stone);
    m.position.set(x, y, z);
    m.rotation.x = rx;
    m.castShadow = m.receiveShadow = true;
    group.add(m);
    return m;
  };
  part(h * 0.4, h * 0.08, h * 0.12, 0, h * 0.72, h * 0.19);            // brow
  part(h * 0.1, h * 0.34, h * 0.1, 0, h * 0.52, h * 0.22, -0.18);      // nose
  part(h * 0.22, h * 0.035, h * 0.06, 0, h * 0.3, h * 0.2);            // lips
  part(h * 0.3, h * 0.12, h * 0.1, 0, h * 0.18, h * 0.18);             // chin
  for (const s of [-1, 1]) part(h * 0.05, h * 0.3, h * 0.08, s * h * 0.23, h * 0.58, 0); // long ears
  group.traverse((o) => { if (o.isMesh) o.castShadow = o.receiveShadow = true; });
  return group;
}

/* ---------- Out on the planet ---------- */

/**
 * A retro radio telescope: a concrete pedestal, a yoke you could imagine turning, a white
 * parabolic dish with panel seams and ribs behind it, and a feed horn held out on three struts.
 * About 2.8 m tall. Faces local +z, tipped back toward the sky.
 */
/**
 * Launch control: a mid-century steel console beside the pad (`shell`: the Meshy model, or
 * cream and teal boxes without it), status lamps, a red button under a guard, and a screen
 * showing the next launch anywhere and its countdown (`show(lines)`: up to four lines, redrawn
 * when they change). Faces +z; about 1.6 m tall (1.45 from boxes). `blink(t)` runs its lamps.
 */
export function launchConsole({ shell = null } = {}) {
  const g = new THREE.Group();
  const body = pbr({ color: PALETTE.cream, roughness: 0.55 });
  const teal = pbr({ color: PALETTE.tinTeal, roughness: 0.45, metalness: 0.1 });
  const dark = pbr({ color: PALETTE.lava, roughness: 0.5, metalness: 0.3 });
  const steel = pbr({ color: PALETTE.chrome, metalness: 0.7, roughness: 0.35 });
  const add = (geo, mat, [x, y, z], parent = g) => { const m = new THREE.Mesh(geo, mat); m.position.set(x, y, z); m.castShadow = m.receiveShadow = true; parent.add(m); return m; };
  // the lamps say the next launch's status (green go, amber to be confirmed, red on hold),
  // chasing slowly, quickly in its last hour
  const lamps = [];
  const LAMP = { go: glow(0x4dff9a, 3), tbd: glow(PALETTE.amber, 3), hold: glow(PALETTE.coral, 3) };
  const lamp = (r, at, parent) => {
    const l = add(new THREE.SphereGeometry(r, 10, 8), LAMP.tbd, at, parent);
    l.castShadow = false;
    l.userData.noBatch = true;
    lamps.push(l);
  };
  // The body: the Meshy model (hero slot "launch-console") when it's there, its manifest
  // `anchors` saying where its screen, the top of its screen housing and its desk are (metres,
  // in the scaled model); otherwise built from boxes. Either way the parts that change (the
  // screen, the lamps, the red button and its guard) are added here.
  const fit = shell && shell.userData.spec && shell.userData.spec.anchors;
  let panel, head, screenW = 0.64, screenH = 0.4, screenZ = 0.127, buttonAt = [0.42, 0.02];
  if (fit) {
    g.add(shell);
    const [sx, sy, sz, sw, sh] = fit.screen;
    head = new THREE.Group();
    head.position.set(sx, sy, sz);
    g.add(head);
    [screenW, screenH, screenZ] = [sw, sh, 0];
    // a row of lamps along the top of the screen housing, each in a chrome cup
    const [ly, lz, span] = fit.lamps;
    for (let i = 0; i < 6; i++) {
      const x = -span / 2 + (i * span) / 5;
      add(new THREE.CylinderGeometry(0.03, 0.034, 0.016, 14), steel, [x, ly + 0.008, lz]);
      lamp(0.024, [x, ly + 0.022, lz]);
    }
    // the red button on the desk, its top at the desk's surface
    const [bx, by, bz] = fit.button;
    panel = new THREE.Group();
    panel.position.set(bx, by - 0.02, bz);
    g.add(panel);
    buttonAt = [0, 0];
  } else {
    // the desk: a plinth, the body, a teal kick band, and the slanted panel
    add(new THREE.BoxGeometry(1.3, 0.08, 0.7), dark, [0, 0.04, 0]);
    add(new THREE.BoxGeometry(1.2, 0.7, 0.55), body, [0, 0.43, -0.02]);
    add(new THREE.BoxGeometry(1.22, 0.1, 0.57), teal, [0, 0.13, -0.02]);
    panel = new THREE.Group();
    panel.position.set(0, 0.82, 0.12);
    panel.rotation.x = -0.55;
    g.add(panel);
    add(new THREE.BoxGeometry(1.2, 0.04, 0.42), teal, [0, 0, 0], panel);
    // switches and lamps on the panel
    for (let i = 0; i < 6; i++) {
      const x = -0.45 + i * 0.13;
      const sw = add(new THREE.CylinderGeometry(0.008, 0.008, 0.05, 6), steel, [x, 0.035, 0.08], panel);
      sw.rotation.x = 0.4;
      lamp(0.018, [x, 0.03, -0.06], panel);
    }
    // the screen housing, on a neck, tipped a little toward you
    add(new THREE.CylinderGeometry(0.04, 0.05, 0.25, 12), steel, [0, 0.95, -0.15]);
    head = new THREE.Group();
    head.position.set(0, 1.2, -0.12);
    head.rotation.x = -0.12;
    g.add(head);
    add(new THREE.BoxGeometry(0.78, 0.52, 0.3), body, [0, 0, -0.04], head);
    add(new THREE.BoxGeometry(0.7, 0.44, 0.02), dark, [0, 0, 0.115], head);
  }
  // the red button under its flip-up guard (hinged at its back edge): the engine test
  const [bx, bz] = buttonAt;
  add(new THREE.CylinderGeometry(0.05, 0.055, 0.02, 20), dark, [bx, 0.03, bz], panel);
  const button = add(new THREE.CylinderGeometry(0.035, 0.035, 0.03, 20), pbr({ color: PALETTE.coral, roughness: 0.35, emissive: PALETTE.coral, emissiveIntensity: 0.25 }), [bx, 0.05, bz], panel);
  button.userData.noBatch = true;
  const guard = new THREE.Group();
  guard.position.set(bx, 0.045, bz - 0.065);
  guard.userData.noBatch = true;
  panel.add(guard);
  const stripes = document.createElement('canvas');
  stripes.width = stripes.height = 64;
  { const x = stripes.getContext('2d'); x.fillStyle = '#e8b931'; x.fillRect(0, 0, 64, 64); x.fillStyle = '#1b1b1b'; for (let k = -64; k < 128; k += 22) { x.beginPath(); x.moveTo(k, 0); x.lineTo(k + 11, 0); x.lineTo(k - 53, 64); x.lineTo(k - 64, 64); x.fill(); } }
  const stripeTex = new THREE.CanvasTexture(stripes);
  stripeTex.colorSpace = THREE.SRGBColorSpace;
  const lid = add(new THREE.BoxGeometry(0.12, 0.012, 0.13), pbr({ map: stripeTex, roughness: 0.4 }), [0, 0.03, 0.065], guard);
  lid.castShadow = false;
  for (const x of [-0.055, 0.055]) add(new THREE.BoxGeometry(0.01, 0.03, 0.13), dark, [x, 0.012, 0.065], guard);
  let armed = 0, armTo = 0, pressed = 0;
  // the screen: a canvas, redrawn only when what it says changes
  const W = 512, H = Math.round((W * screenH) / screenW), c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d'), tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(screenW, screenH), new THREE.MeshBasicMaterial({ map: tex, toneMapped: false }));
  screen.position.set(0, 0, screenZ);
  head.add(screen);
  let shown = '';
  function show(lines) {
    const key = lines.join('|');
    if (key === shown) return;
    shown = key;
    ctx.fillStyle = '#071410';
    ctx.fillRect(0, 0, W, H);
    const glowGrad = ctx.createRadialGradient(W / 2, H / 2, 20, W / 2, H / 2, W * 0.7);
    glowGrad.addColorStop(0, 'rgba(60,255,170,.10)'); glowGrad.addColorStop(1, 'rgba(0,0,0,.5)');
    ctx.fillStyle = glowGrad; ctx.fillRect(0, 0, W, H);
    ctx.textAlign = 'left';
    ctx.shadowColor = 'rgba(120,255,190,.8)'; ctx.shadowBlur = 8;
    const [kick, big, name, meta, right] = lines;
    ctx.fillStyle = '#7dffbf';
    ctx.font = '600 22px "JetBrains Mono", ui-monospace, monospace';
    ctx.fillText(kick || '', 28, 48);
    if (right) { ctx.textAlign = 'right'; ctx.fillText(right, W - 28, 48); ctx.textAlign = 'left'; }
    ctx.font = '700 56px "JetBrains Mono", ui-monospace, monospace';
    ctx.fillStyle = '#c8ffe0';
    ctx.fillText(big || '', 28, 128, W - 56);
    ctx.font = '500 26px "JetBrains Mono", ui-monospace, monospace';
    ctx.fillStyle = '#9dffcb';
    const fit = (t, y) => { let s = String(t || ''); while (s.length > 4 && ctx.measureText(s).width > W - 56) s = s.slice(0, -2) + '…'; ctx.fillText(s, 28, y); };
    fit(name, 196);
    ctx.font = '400 20px "JetBrains Mono", ui-monospace, monospace';
    fit(meta, 240);
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(0,0,0,.22)'; // scanlines
    for (let y = 0; y < H; y += 4) ctx.fillRect(0, y, W, 2);
    tex.needsUpdate = true;
  }
  show(['NEXT LAUNCH', 'T- --:--:--', 'Checking the manifest…', '']);
  let status = 'tbd', soon = false;
  return {
    group: g,
    show,
    screen,
    head,
    panel,
    /** The lamps' colour, by the next launch's status ('go', 'tbd', 'hold'); `hurry` chases them fast (its last hour). */
    status(s, hurry = false) { status = LAMP[s] ? s : 'tbd'; soon = hurry; },
    /** Flip the guard up (true) or let it fall shut (false). */
    arm(on) { armTo = on ? 1 : 0; },
    /** Press the button: it goes down and comes back up. */
    press() { pressed = 1; },
    get armed() { return armTo === 1; },
    blink(t, dt = 1 / 60) {
      const m = LAMP[status], rate = soon ? 7 : 2;
      lamps.forEach((l, i) => { l.material = m; l.visible = Math.floor(t * rate + i * (soon ? 1 : 1.7)) % 3 !== 0; });
      armed += (armTo - armed) * Math.min(1, dt * 9);
      guard.rotation.x = -1.95 * armed;
      pressed = Math.max(0, pressed - dt * 2.5);
      button.position.y = 0.05 - 0.014 * Math.min(1, pressed * 3);
    },
  };
}

/**
 * A door to a real place, standing on its own in the grass: two carved tiki posts, a lintel with
 * upswept ends under a little thatch, a lava-rock step, the door itself swung open, a brass label
 * beside the step, and in the opening the place in daylight (portal.js). It draws `view` (an equirectangular
 * panorama from where you come in, tools/portal-view.mjs) by the way you're looking through it,
 * so the view shifts as you walk past, like a window, and stepping through it lines up with
 * the real thing. Until `setView()` it's the place's sky colour. Faces +z (you step through
 * toward -z); base at y = 0. The label names the place and credits the scan (`plaque`: lines).
 */
export function doorway({ sky = 0xcfdde6, plaque = [], eye = 1.6, far = 9, brightness = 1.3, spill: spills = true } = {}) {
  const g = new THREE.Group();
  const OW = 1.0, OH = 2.1, STEP = 0.14; // the opening
  const wood = surface(woodSet(PALETTE.stain), { roughness: 0.8 });
  const dark = surface(woodSet(PALETTE.wood), { roughness: 0.85 });
  const add = (geo, mat, [x, y, z], parent = g) => { const m = new THREE.Mesh(geo, mat); m.position.set(x, y, z); m.castShadow = m.receiveShadow = true; parent.add(m); return m; };
  // the step: a slab of lava rock, the threshold in dark wood
  const { map, bump } = lavaSet();
  add(new THREE.BoxGeometry(1.7, STEP, 0.9), pbr({ map, bumpMap: bump, bumpScale: 2, color: 0x6a5a52, roughness: 0.95 }), [0, STEP / 2, 0.05]);
  add(new THREE.BoxGeometry(OW + 0.1, 0.03, 0.18), dark, [0, STEP + 0.015, 0]);
  // the posts: pop tikis, a face each at eye height
  for (const x of [-1, 1]) {
    const post = carvedTiki({ height: 2.55, radius: 0.16, style: 'ku' });
    post.position.set(x * (OW / 2 + 0.17), STEP, 0);
    g.add(post);
  }
  // the jambs and head inside the posts, so the opening has a clean edge
  for (const x of [-1, 1]) add(new THREE.BoxGeometry(0.07, OH, 0.2), dark, [x * (OW / 2 + 0.035), STEP + OH / 2, 0]);
  add(new THREE.BoxGeometry(OW + 0.14, 0.08, 0.2), dark, [0, STEP + OH + 0.04, 0]);
  // the lintel, its ends swept up like the bar's ridge, and a short thatch cap on it
  const top = STEP + 2.55;
  add(new THREE.BoxGeometry(1.9, 0.2, 0.3), wood, [0, top + 0.1, 0]);
  for (const x of [-1, 1]) {
    const horn = add(new THREE.BoxGeometry(0.34, 0.12, 0.26), wood, [x * 1.06, top + 0.2, 0]);
    horn.rotation.z = x * 0.55;
  }
  const thatch = surface(thatchSet(), { roughness: 1 });
  for (const z of [-1, 1]) {
    const side = add(new THREE.BoxGeometry(1.75, 0.06, 0.34), thatch, [0, top + 0.32, z * 0.12]);
    side.rotation.x = z * 0.6;
  }
  // the door, swung all the way open into the other side, hinged on the left
  const leaf = new THREE.Group();
  leaf.position.set(-OW / 2, STEP + 0.02, -0.06);
  leaf.rotation.y = 1.75;
  g.add(leaf);
  for (let i = 0; i < 4; i++) add(new THREE.BoxGeometry(OW / 4 - 0.01, OH - 0.04, 0.045), i % 2 ? wood : dark, [OW / 8 + i * OW / 4, (OH - 0.04) / 2, 0], leaf);
  for (const y of [0.35, OH - 0.45]) add(new THREE.BoxGeometry(OW - 0.06, 0.1, 0.03), wood, [OW / 2, y, 0.035], leaf);
  // the opening: the place, looked into (doorwindow.js)
  const uniforms = { view: { value: null }, ready: { value: 0 }, sky: { value: new THREE.Color(sky) }, brightness: { value: brightness }, far: { value: far }, centre: { value: new THREE.Vector3(0, eye - STEP - OH / 2, 0.16) } };
  const opening = new THREE.Mesh(new THREE.PlaneGeometry(OW, OH), new THREE.ShaderMaterial({
    uniforms,
    vertexShader: windowVertex,
    fragmentShader: windowFragment,
  }));
  opening.position.set(0, STEP + OH / 2, 0);
  opening.castShadow = opening.receiveShadow = false;
  opening.userData.noBatch = true;
  g.add(opening);
  // daylight spilling out onto the step and the grass in front (not from the door back: night doesn't spill)
  if (spills) {
    const spill = new THREE.Mesh(new THREE.PlaneGeometry(1.5, 2.2, 6, 10).rotateX(-Math.PI / 2).translate(0, 0, 1.15), new THREE.MeshBasicMaterial({
      map: spillTexture(), color: sky, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
    }));
    spill.position.y = 0.02;
    spill.userData.noBatch = true;
    spill.renderOrder = 2;
    g.add(spill);
  }
  // a small brass plaque: where this is, and whose scan it is
  if (plaque.length) {
    const c = document.createElement('canvas');
    c.width = 512; c.height = 256;
    const x = c.getContext('2d');
    x.fillStyle = '#7a5a2a'; x.fillRect(0, 0, 512, 256);
    x.fillStyle = '#c9a46b'; x.fillRect(10, 10, 492, 236);
    x.fillStyle = '#3a2a12'; x.textAlign = 'center';
    plaque.forEach((line, i) => {
      x.font = i ? '600 34px Inter, system-ui, sans-serif' : '700 58px Fraunces, Georgia, serif';
      x.fillText(line, 256, i ? 128 + i * 50 : 92, 468);
    });
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    // on a short stake of its own beside the step, tipped back toward you, like a museum label
    const label = new THREE.Group();
    label.position.set(-(OW / 2 + 0.62), 0, 0.62);
    label.rotation.y = 0.35;
    g.add(label);
    add(new THREE.CylinderGeometry(0.025, 0.03, 0.8, 8), dark, [0, 0.4, 0], label);
    const brass = pbr({ color: 0x8a6a35, metalness: 0.8, roughness: 0.35 });
    const plate = add(new THREE.BoxGeometry(0.42, 0.21, 0.014), [brass, brass, brass, brass, pbr({ map: tex, metalness: 0.5, roughness: 0.45 }), brass], [0, 0.86, 0.02], label);
    plate.rotation.x = -0.7;
    plate.castShadow = false;
  }
  g.userData.opening = new THREE.Vector3(0, STEP + OH / 2, 0);
  return {
    group: g,
    opening,
    /** The panorama has loaded: show it. */
    setView(tex) { uniforms.view.value = tex; uniforms.ready.value = 1; },
  };
}

// a soft pool, brightest at the threshold and fading out across the grass
function spillTexture() {
  const c = document.createElement('canvas');
  c.width = 64; c.height = 128;
  const x = c.getContext('2d');
  const img = x.createImageData(64, 128);
  for (let j = 0; j < 128; j++) for (let i = 0; i < 64; i++) {
    const v = 1 - j / 127, u = (i / 63 - 0.5) * 2; // laid flat, the canvas's top row is at the threshold: v 1 there, 0 at the far end
    const w = 0.55 + (1 - v) * 0.45; // it widens as it goes out, like light through a door
    const a = Math.max(0, 1 - Math.abs(u) / w) ** 1.5 * v ** 1.6;
    img.data.set([255, 255, 255, Math.round(a * 255)], (j * 64 + i) * 4);
  }
  x.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export function radioDish() {
  const g = new THREE.Group();
  const concrete = surface(lavaSet(), { color: PALETTE.ash, roughness: 0.95, bumpScale: 1 });
  const white = pbr({ color: PALETTE.rocketWhite, roughness: 0.45, metalness: 0.1, side: THREE.DoubleSide });
  const steel = pbr({ color: PALETTE.chrome, metalness: 0.6, roughness: 0.4 });
  const dark = pbr({ color: PALETTE.lava, metalness: 0.4, roughness: 0.6 });
  // an octagonal plinth and a round pedestal
  const plinth = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.78, 0.3, 8), concrete);
  plinth.position.y = 0.15;
  g.add(plinth);
  const pedestal = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.3, 0.9, 16), concrete);
  pedestal.position.y = 0.75;
  g.add(pedestal);
  // the turntable and yoke
  const turn = new THREE.Group();
  turn.position.y = 1.22;
  g.add(turn);
  const table = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.08, 20), dark);
  turn.add(table);
  for (const x of [-0.42, 0.42]) {
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.6, 0.16), white);
    arm.position.set(x, 0.3, 0);
    turn.add(arm);
  }
  // the dish on its elevation axis, tipped back to look up
  const tilt = new THREE.Group();
  tilt.position.y = 0.56;
  tilt.rotation.x = -0.75;
  turn.add(tilt);
  const axle = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.92, 12), steel);
  axle.rotation.z = Math.PI / 2;
  tilt.add(axle);
  const R = 1.1, DEPTH = 0.32;
  const bowl = [];
  for (let i = 0; i <= 12; i++) { const r = (i / 12) * R; bowl.push(new THREE.Vector2(r, (r * r) / (R * R) * DEPTH)); }
  const dishGeo = new THREE.LatheGeometry(bowl, 48).rotateX(Math.PI / 2); // opening toward +z
  const dish = new THREE.Mesh(dishGeo, white);
  dish.position.z = 0.12;
  dish.castShadow = true;
  tilt.add(dish);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(R, 0.025, 8, 48), white);
  rim.position.z = 0.12 + DEPTH;
  tilt.add(rim);
  // panel seams on the face, and ribs across the back
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * TAU;
    const seam = [];
    for (let k = 1; k <= 8; k++) { const r = (k / 8) * R; seam.push(new THREE.Vector3(Math.cos(a) * r, Math.sin(a) * r, 0.12 + (r * r) / (R * R) * DEPTH + 0.006)); }
    tilt.add(new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(seam), 12, 0.005, 4), pbr({ color: PALETTE.stone })));
  }
  // behind it, a hub, eight ribs following the curve and a ring truss tying them together
  const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.2, 0.14, 16), dark);
  hub.rotation.x = Math.PI / 2;
  hub.position.z = 0.05;
  tilt.add(hub);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * TAU, pts = [];
    for (let k = 0; k <= 6; k++) { const r = 0.15 + (k / 6) * (R - 0.17); pts.push(new THREE.Vector3(Math.cos(a) * r, Math.sin(a) * r, 0.1 + (r * r) / (R * R) * DEPTH - 0.04)); }
    tilt.add(new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 12, 0.018, 5), dark));
  }
  const truss = new THREE.Mesh(new THREE.TorusGeometry(R * 0.62, 0.014, 6, 40), dark);
  truss.position.z = 0.1 + 0.38 * DEPTH - 0.04;
  tilt.add(truss);
  // the feed horn out front on three struts, with its beacon mount
  const feedZ = 0.12 + DEPTH + 0.75;
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * TAU + Math.PI / 2;
    const from = new THREE.Vector3(Math.cos(a) * R * 0.92, Math.sin(a) * R * 0.92, 0.12 + DEPTH * 0.85);
    const to = new THREE.Vector3(0, 0, feedZ);
    const d = to.clone().sub(from);
    const s = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, d.length(), 6), steel);
    s.position.copy(from).addScaledVector(d, 0.5);
    s.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
    tilt.add(s);
  }
  const horn = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.1, 0.22, 16), white);
  horn.rotation.x = Math.PI / 2;
  horn.position.z = feedZ - 0.02;
  tilt.add(horn);
  // a little equipment hut at the foot, with a lit window
  const hut = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.36, 0.3), white);
  hut.position.set(0.52, 0.48, -0.36);
  g.add(hut);
  const win = new THREE.Mesh(new THREE.PlaneGeometry(0.14, 0.08), glow(PALETTE.amber, 1.4));
  win.position.set(0.52, 0.52, -0.2);
  win.rotation.y = 0;
  g.add(win);
  g.traverse((o) => { if (o.isMesh) o.castShadow = o.receiveShadow = true; });
  // all of it the stand-in for the Meshy dish (hero slot "radio-dish", facing +z and tipped up
  // the same way, loaded once the planet is up); `userData.onModel()` runs when it arrives
  const stand = new THREE.Group();
  stand.add(...g.children);
  g.add(stand);
  heroSlot('radio-dish', stand, { onFill: () => g.userData.onModel && g.userData.onModel() });
  return g;
}

/**
 * A Polynesian outrigger canoe (va'a), pulled up on the beach: a carved hull with raised ends,
 * a float (ama) off to one side on two curved booms (iako) lashed across, and a paddle.
 * About 3 m long, along local z.
 */
export function outriggerCanoe() {
  const g = new THREE.Group();
  const wood = surface(woodSet(PALETTE.wood), { roughness: 0.75 });
  const stain = surface(woodSet(PALETTE.stain), { roughness: 0.8 });
  // hull: the lower half of a long, narrow spindle, open at the top
  const hullGeo = new THREE.SphereGeometry(1, 28, 10, 0, TAU, Math.PI / 2, Math.PI / 2);
  const hp = hullGeo.attributes.position;
  for (let i = 0; i < hp.count; i++) {
    const z = hp.getZ(i);
    hp.setX(i, hp.getX(i) * (1 - Math.abs(z) * 0.25)); // finer at the ends
  }
  hullGeo.computeVertexNormals();
  const hull = new THREE.Mesh(hullGeo, surface(woodSet(PALETTE.wood), { roughness: 0.75, side: THREE.DoubleSide }));
  hull.scale.set(0.3, 0.28, 1.5);
  hull.position.y = 0.26;
  g.add(hull);
  // gunwales and the raised, carved ends
  for (const x of [-0.29, 0.29]) {
    const gw = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.035, 2.7), stain);
    gw.position.set(x * 0.95, 0.27, 0);
    g.add(gw);
  }
  for (const z of [-1, 1]) {
    const end = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.45, 4), stain);
    end.position.set(0, 0.42, z * 1.45);
    end.rotation.x = z * 0.9;
    g.add(end);
  }
  // the float and the two booms that carry it, lashed to the gunwales
  const ama = new THREE.Mesh(new THREE.CapsuleGeometry(0.08, 1.4, 6, 12), wood);
  ama.rotation.x = Math.PI / 2;
  ama.position.set(1.15, 0.08, 0);
  g.add(ama);
  for (const z of [-0.55, 0.55]) {
    const boom = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3([
      new THREE.Vector3(-0.3, 0.3, z), new THREE.Vector3(0.45, 0.36, z), new THREE.Vector3(1.0, 0.24, z), new THREE.Vector3(1.15, 0.12, z)]), 16, 0.025, 6), stain);
    g.add(boom);
    const knot = new THREE.Mesh(new THREE.TorusGeometry(0.035, 0.012, 6, 10), pbr({ color: PALETTE.thatch, roughness: 1 }));
    knot.position.set(0.29, 0.29, z);
    knot.rotation.y = Math.PI / 2;
    g.add(knot);
  }
  // a paddle laid across the hull
  const paddle = new THREE.Group();
  paddle.position.set(-0.05, 0.31, 0.2);
  paddle.rotation.set(0, 0.5, 0.05);
  g.add(paddle);
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 1.1, 8), wood);
  shaft.rotation.x = Math.PI / 2;
  paddle.add(shaft);
  const blade = new THREE.Mesh(new THREE.SphereGeometry(1, 12, 8), stain);
  blade.scale.set(0.09, 0.012, 0.24);
  blade.position.z = 0.72;
  paddle.add(blade);
  g.traverse((o) => { if (o.isMesh) o.castShadow = o.receiveShadow = true; });
  // all of it the stand-in for the Meshy canoe (hero slot "outrigger-canoe", turned to lie along z)
  const stand = new THREE.Group();
  stand.add(...g.children);
  g.add(stand);
  heroSlot('outrigger-canoe', stand);
  return g;
}

/*
 * A striped canvas hammock slung between `a` and `b` (world points where it's tied to two trunks),
 * with `up` the local up. The cloth sags in a curve and its edges curl up; fan lines gather each
 * end to a rope round the trunk; an open book lies in it. Returns the group, already in place.
 */
/**
 * A striped hammock slung between two points (`a`, `b`: the ties, world space) with an open book
 * left in it. Its frame: x runs from tie to tie, y is up, z across. `userData.hammock` lets you
 * get in: the cloth and its fan lines hang in `swing`, which swings about the line between the
 * ties (a damped pendulum: `kick()` it, `update(dt)` it), `setLoad(k)` sags it under your weight,
 * and `point(u, lift, across)` gives a spot on the cloth's centre line (u 0..1 along it) in
 * `swing`'s frame, so a body lying in it swings with it.
 */
export function hammock(a, b, up, { sag = 0.95, width = 0.9 } = {}) {
  const g = new THREE.Group();
  const x = b.clone().sub(a), L = x.length();
  x.normalize();
  const z = x.clone().cross(up).normalize(), y = z.clone().cross(x);
  g.matrixAutoUpdate = true;
  g.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z));
  g.position.copy(a);
  const swing = new THREE.Group(); // pivots on the tie line (local x through the origin)
  g.add(swing);
  const END = 0.5; // rope from each tie to the cloth
  const cloth = L - 2 * END;
  let load = 0; // 0..1: someone's weight in it
  const WEIGHT = 0.16; // how much deeper it hangs with you in it (m)
  const sagAt = (u) => -sag * 4 * u * (1 - u) - WEIGHT * load * Math.sin(Math.PI * u);
  const halfW = (u) => (width / 2) * (0.2 + 0.8 * Math.pow(Math.sin(Math.PI * u), 0.5));
  const geo = new THREE.PlaneGeometry(1, 1, 32, 10);
  const pos = geo.attributes.position;
  const uv0 = Array.from({ length: pos.count }, (_, i) => [pos.getX(i) + 0.5, pos.getY(i) * 2]); // u along, v across (-1..1)
  const shape = () => {
    for (let i = 0; i < pos.count; i++) {
      const [u, v] = uv0[i], w = halfW(u);
      pos.setXYZ(i, END + u * cloth, sagAt(u) + 0.2 * v * v * Math.sin(Math.PI * u) - 0.12, v * w);
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
    geo.computeBoundingSphere();
  };
  shape();
  const mesh = new THREE.Mesh(geo, pbr({ map: T.hammockCloth(), roughness: 0.9, side: THREE.DoubleSide }));
  mesh.castShadow = mesh.receiveShadow = true;
  swing.add(mesh);
  // fan lines: from each tie to points across the cloth's gathered end (they swing with it)
  const rope = pbr({ color: PALETTE.bamboo, roughness: 0.9 });
  const line = (p, q) => {
    const d = q.clone().sub(p), m = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, d.length(), 4), rope);
    m.position.copy(p).add(q).multiplyScalar(0.5);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
    swing.add(m);
  };
  for (const [tie, u] of [[new THREE.Vector3(0, 0, 0), 0.02], [new THREE.Vector3(L, 0, 0), 0.98]]) {
    for (const v of [-1, -0.5, 0, 0.5, 1]) line(tie, new THREE.Vector3(END + u * cloth, sagAt(u) - 0.12 + 0.2 * v * v * Math.sin(Math.PI * u), v * halfW(u)));
    const wrap = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.012, 5, 16), rope); // round the trunk
    wrap.position.copy(tie); wrap.rotation.x = Math.PI / 2;
    g.add(wrap);
  }
  // an open book, face down where someone left off
  const bu = 0.42;
  const book = new THREE.Group();
  book.position.set(END + bu * cloth, sagAt(bu) - 0.12 + 0.03, 0.08);
  book.rotation.set(0, 0.5, 0);
  // the pages catch a little starlight, so an open book reads as one in the dark
  const cover = pbr({ color: PALETTE.coral, roughness: 0.7 }), pageMap = T.bookPage(), pages = pbr({ color: 0xffffff, map: pageMap, roughness: 0.9, emissive: 0xffffff, emissiveMap: pageMap, emissiveIntensity: 0.12 });
  const halves = [-1, 1].map((s) => {
    const half = new THREE.Group();
    half.rotation.x = s * 0.35; // a tent, spine up
    half.userData.side = s;
    book.add(half);
    const c = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.006, 0.14), cover); c.position.set(0, 0.003, s * 0.07); half.add(c);
    const p2 = new THREE.Mesh(new THREE.BoxGeometry(0.19, 0.012, 0.13), pages); p2.position.set(0, -0.006, s * 0.065); half.add(p2);
    return half;
  });
  swing.add(book);
  const bookHome = { pos: book.position.clone(), quat: book.quaternion.clone() };

  // the swing: a damped pendulum about the tie line
  let angle = 0, vel = 0;
  const OMEGA = 2.9, DAMP = 0.55; // about a 2 s period; it settles in a few swings
  g.userData.hammock = {
    group: g, swing, book, halves, bookHome, length: L,
    /** A spot on the cloth's centre line (u 0..1 along the cloth), `lift` above it and `across` to the side, in `swing`'s frame. */
    point(u, lift = 0, across = 0) { return new THREE.Vector3(END + u * cloth, sagAt(u) - 0.12 + lift, across); },
    setLoad(k) { if (Math.abs(k - load) < 1e-3) return; load = k; shape(); book.position.y = bookHome.pos.y - WEIGHT * load * Math.sin(Math.PI * bu); },
    get load() { return load; },
    kick(v) { vel += v; },
    get angle() { return angle; },
    update(dt, still = false) {
      if (still) { angle = vel = 0; swing.rotation.x = 0; return; }
      if (!angle && !vel) return;
      const h = Math.min(dt, 1 / 30);
      vel += (-OMEGA * OMEGA * angle - 2 * DAMP * OMEGA * vel) * h;
      angle += vel * h;
      if (Math.abs(angle) < 1e-4 && Math.abs(vel) < 1e-3) angle = vel = 0;
      swing.rotation.x = angle;
    },
  };
  return g;
}

/** A small stack of books to leave on the sand: a few colours, slightly askew. */
export function bookStack(n = 3, seed = 9) {
  const r = rng(seed);
  const g = new THREE.Group();
  const cols = [PALETTE.tinTeal, PALETTE.coral, PALETTE.amber, PALETTE.leaf, PALETTE.cream];
  let y = 0;
  for (let i = 0; i < n; i++) {
    const h = 0.035 + r() * 0.03, w = 0.17 + r() * 0.06, d = 0.23 + r() * 0.06;
    const m = new THREE.Mesh(new THREE.BoxGeometry(d, h, w), pbr({ color: cols[i % cols.length], roughness: 0.75 }));
    m.position.y = y + h / 2; m.rotation.y = (r() - 0.5) * 0.6;
    m.castShadow = m.receiveShadow = true;
    g.add(m);
    y += h;
  }
  return g;
}

/**
 * Messages in bottles washed up at the waterline: green glass, a cork, a rolled letter inside.
 * `n` bottles in a loose group, one stuck upright in the sand.
 */
/**
 * One message in a bottle, standing on its base (y = 0): a green glass bottle, a cork, and the
 * rolled letter inside. userData { cork, letter, neck } (neck: the mouth's height).
 */
const BOTTLE_PROFILE = [[0, 0], [0.045, 0], [0.05, 0.01], [0.05, 0.15], [0.042, 0.18], [0.02, 0.21], [0.016, 0.24], [0.018, 0.26], [0, 0.26]];
let bottleParts = null;
export function messageBottle() {
  bottleParts ||= {
    geo: new THREE.LatheGeometry(BOTTLE_PROFILE.map(([x, y]) => new THREE.Vector2(x, y)), 16),
    glass: new THREE.MeshStandardMaterial({ color: 0x3f8f5a, roughness: 0.12, metalness: 0, transparent: true, opacity: 0.5, depthWrite: false }),
    cork: pbr({ color: PALETTE.thatch, roughness: 0.9 }),
    letter: pbr({ color: PALETTE.cream, roughness: 0.9 }),
  };
  const b = new THREE.Group();
  const glass = new THREE.Mesh(bottleParts.geo, bottleParts.glass);
  glass.renderOrder = 1; // drawn after what's inside it
  // the glass is the stand-in for the Meshy bottle (hero slot "message-bottle", 0.26 m to its
  // mouth, loaded once the planet is up), made see-through so the letter shows
  const shell = new THREE.Group().add(glass);
  b.add(shell);
  heroSlot('message-bottle', shell, { fit: seeThrough });
  const cork = new THREE.Mesh(new THREE.CylinderGeometry(0.017, 0.015, 0.03, 8), bottleParts.cork); cork.position.y = 0.265; b.add(cork);
  const letter = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.12, 10), bottleParts.letter); letter.position.y = 0.08; letter.rotation.z = 0.08; b.add(letter);
  b.userData = { cork, letter, neck: 0.26 };
  return b;
}

function seeThrough(model) {
  model.traverse((o) => {
    if (!o.isMesh) return;
    o.renderOrder = 1;
    const m = o.material;
    if (m.userData.seeThrough) return;
    Object.assign(m, { transparent: true, opacity: 0.62, depthWrite: false, roughness: 0.15 });
    m.userData.seeThrough = true;
  });
}

/** A few of them washed up at the waterline; the last stands upright (userData.upright). */
export function messageBottles(n = 3, seed = 17) {
  const r = rng(seed);
  const g = new THREE.Group();
  for (let i = 0; i < n; i++) {
    const b = messageBottle();
    const upright = i === n - 1;
    const a = (i / n) * TAU + r() * 0.8, rr = 0.15 + r() * 0.25;
    b.position.set(Math.cos(a) * rr, upright ? -0.05 : 0.04, Math.sin(a) * rr);
    b.rotation.set(upright ? 0.25 : Math.PI / 2 - 0.08, r() * TAU, upright ? 0.15 : 0);
    if (!upright) b.rotation.order = 'YXZ';
    else g.userData.upright = b;
    g.add(b);
  }
  return g;
}

/**
 * A tiki signpost: a wooden post with a small carved head on top and arrow boards, one per
 * destination ([{ text, angle }], angle in radians about the post, 0 = local +x), stacked down
 * the post, lettered on both faces.
 */
export function signpost(boards, { height = 1.9 } = {}) {
  const g = new THREE.Group();
  const wood = surface(woodSet(PALETTE.wood), { roughness: 0.8 });
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.075, height, 10), wood);
  post.position.y = height / 2;
  post.castShadow = true;
  const head = carvedTiki({ height: 0.42, radius: 0.1, style: 'ku' });
  head.position.y = height;
  // the post and its head are the stand-in for the Meshy post (hero slot "signpost": a pole with a
  // carved face at the top); the boards stay, lettered here
  g.add(heroSlot('signpost', new THREE.Group().add(post, head), { fit: (model) => model.scale.setScalar((height + 0.25) / model.userData.spec.height) }));
  const W = 0.64, H = 0.13, TIP = 0.1, D = 0.03;
  const shape = new THREE.Shape();
  shape.moveTo(0, -H / 2); shape.lineTo(W - TIP, -H / 2); shape.lineTo(W, 0); shape.lineTo(W - TIP, H / 2); shape.lineTo(0, H / 2); shape.closePath();
  const plankGeo = new THREE.ExtrudeGeometry(shape, { depth: D, bevelEnabled: false }).translate(0.05, 0, -D / 2);
  const plank = surface(woodSet(PALETTE.stain), { roughness: 0.85 });
  boards.forEach(({ text, angle }, i) => {
    const board = new THREE.Group();
    board.position.y = height - 0.2 - i * (H + 0.045);
    board.rotation.y = angle + (i % 2 ? 0.04 : -0.04); // a little hand-hammered
    const m = new THREE.Mesh(plankGeo, plank);
    m.castShadow = true;
    board.add(m);
    const tex = T.signLettering(text);
    const face = new THREE.MeshStandardMaterial({ map: tex, transparent: true, roughness: 0.8, depthWrite: false });
    for (const s of [1, -1]) {
      const f = new THREE.Mesh(new THREE.PlaneGeometry(W - TIP - 0.04, H * 0.86), face);
      f.position.set(0.05 + (W - TIP) / 2 + 0.01, 0, s * (D / 2 + 0.002));
      if (s < 0) f.rotation.y = Math.PI;
      board.add(f);
    }
    g.add(board);
  });
  return g;
}

/**
 * A stand-in Thinker in bronze, from simple forms, for when the hero model isn't there: a
 * figure seated on a rock, bent forward, chin on the back of the right hand, the right elbow on
 * the left knee. About 1.35 m tall; base at y = 0; faces +z.
 */
export function thinkerFallback() {
  const g = new THREE.Group();
  const bronze = pbr({ color: PALETTE.bronze, metalness: 0.6, roughness: 0.45 });
  const add = (geo, x, y, z, rx = 0, ry = 0, rz = 0) => { const m = new THREE.Mesh(geo, bronze); m.position.set(x, y, z); m.rotation.set(rx, ry, rz); m.castShadow = true; g.add(m); return m; };
  const limb = (r, len) => new THREE.CapsuleGeometry(r, len, 4, 10);
  add(new THREE.DodecahedronGeometry(0.34, 1), 0, 0.3, -0.05).scale.set(1.1, 0.9, 1); // the rock
  add(limb(0.14, 0.34), 0, 0.82, 0.02, 0.55, 0, 0);          // torso, bent forward
  add(new THREE.SphereGeometry(0.1, 16, 12), 0, 1.2, 0.26);   // head, down
  for (const s of [-1, 1]) {
    add(limb(0.07, 0.34), s * 0.1, 0.62, 0.2, Math.PI / 2, 0, 0);   // thighs, forward
    add(limb(0.055, 0.34), s * 0.12, 0.33, 0.4, 0.15, 0, 0);          // shins, down
  }
  add(limb(0.045, 0.28), 0.12, 0.88, 0.22, 0.2, 0, 0.45);        // right upper arm, down to the knee
  add(limb(0.04, 0.26), 0.02, 1.02, 0.32, -0.3, 0, -0.25);       // right forearm, up to the chin
  add(limb(0.045, 0.3), -0.18, 0.78, 0.2, 0.7, 0, -0.2);         // left arm over the left knee
  return g;
}

/* ---------------- Glassware, for the drinks the robot makes ---------------- */

// Profiles (outer wall, bottom to rim) in metres, for LatheGeometry. `base` is the inside floor.
const GLASSES = {
  rocks:   { r: 0.043, h: 0.09, base: 0.014, wall: 0.004 },  // old fashioned
  collins: { r: 0.031, h: 0.15, base: 0.014, wall: 0.0035 },
  coupe:   { r: 0.056, h: 0.125, base: 0.085, wall: 0.003, stem: true }, // bowl from 0.085 up
};

/**
 * A glass for a real drink: clear glass, the drink inside (fill it as it's poured), ice,
 * foam, frost and garnish. Stands on y = 0; `mouth` is the top centre, in its own frame.
 */
export function barGlass(kind = 'rocks') {
  const G = GLASSES[kind] || GLASSES.rocks;
  const group = new THREE.Group();
  const glassMat = new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0.05, metalness: 0, transparent: true, opacity: 0.13, clearcoat: 1, depthWrite: false }); // the near wall only: the far one hazed the drink
  const pts = [];
  if (G.stem) {
    // foot, stem, and a wide shallow bowl
    pts.push(new THREE.Vector2(0.001, 0), new THREE.Vector2(0.036, 0), new THREE.Vector2(0.036, 0.004), new THREE.Vector2(0.006, 0.01), new THREE.Vector2(0.004, 0.07), new THREE.Vector2(0.01, G.base - 0.006));
    for (let i = 0; i <= 10; i++) { const a = (i / 10) * Math.PI * 0.5; pts.push(new THREE.Vector2(Math.sin(a) * G.r, G.base - 0.004 + (1 - Math.cos(a)) * (G.h - G.base))); }
  } else {
    pts.push(new THREE.Vector2(0.001, 0), new THREE.Vector2(G.r * 0.96, 0), new THREE.Vector2(G.r, 0.006), new THREE.Vector2(G.r, G.h));
  }
  const shell = new THREE.Mesh(new THREE.LatheGeometry(pts, 28), glassMat);
  shell.renderOrder = 2; // after what's inside it
  group.add(shell);
  // a heavy clear base on the tumblers, which is what makes them read as glass in low light
  if (!G.stem) {
    const foot = new THREE.Mesh(new THREE.CylinderGeometry(G.r - G.wall, G.r * 0.96, G.base, 24), glassMat.clone());
    foot.material.opacity = 0.35;
    foot.position.y = G.base / 2;
    foot.renderOrder = 2;
    group.add(foot);
  }

  // The drink: straight glasses fill as a cylinder; the coupe's bowl as a cap that widens.
  const inner = G.r - G.wall;
  const depth = (G.stem ? G.h - G.base : G.h - G.base) * 0.9;
  // solid, so it reads as a full drink from the stool (see-through, the glass's base and the
  // counter showed through it and it looked hollow); the ice shows where it rides above it
  const liquidMat = pbr({ color: PALETTE.amber, emissive: PALETTE.amber, emissiveIntensity: 0.05, roughness: 0.1 }); // a little glow to read in the dark; more washes reds out to pink
  let liquid;
  if (G.stem) {
    const cap = [];
    for (let i = 0; i <= 10; i++) { const a = (i / 10) * Math.PI * 0.5; cap.push(new THREE.Vector2(Math.sin(a) * inner * 0.97, (1 - Math.cos(a)))); }
    cap.push(new THREE.Vector2(0, 1));
    liquid = new THREE.Mesh(new THREE.LatheGeometry(cap, 24), liquidMat);
    liquid.position.y = G.base - 0.002;
  } else {
    liquid = new THREE.Mesh(new THREE.CylinderGeometry(inner * 0.98, inner * 0.98, 1, 24).translate(0, 0.5, 0), liquidMat);
    liquid.position.y = G.base;
  }
  liquid.visible = false;
  group.add(liquid);
  let level = 0;
  const fill = (k) => {
    level = THREE.MathUtils.clamp(k, 0, 1);
    liquid.visible = level > 0.01;
    if (G.stem) { // the bowl is a quarter circle: scale it so its top sits at the fill height, as wide as the bowl is there
      const hgt = Math.max(0.001, level * depth), a = Math.acos(1 - hgt / (G.h - G.base));
      liquid.scale.set(Math.sin(a), hgt, Math.sin(a));
    } else liquid.scale.y = Math.max(0.001, level * depth);
    const top = liquid.position.y + level * depth;
    for (const f of floaters) f.position.y = Math.max(f.userData.rest, top - f.userData.sink);
  };

  // Ice floats on what's poured; crushed ice fills the glass. Garnish and foam sit on top.
  const floaters = [];
  const iceMat = new THREE.MeshPhysicalMaterial({ color: PALETTE.ice, roughness: 0.25, transparent: true, opacity: 0.6, clearcoat: 1, depthWrite: false });
  function ice(type) {
    if (type === 'cubes') {
      const cube = new THREE.BoxGeometry(0.026, 0.026, 0.026);
      [[-0.01, 0.009, 0.3], [0.012, -0.006, 1.1], [-0.002, -0.013, 2.2]].forEach(([x, z, r], i) => {
        const c = new THREE.Mesh(cube, iceMat);
        c.position.set(x, G.base + 0.014 + i * 0.022, z);
        c.rotation.set(r, r * 0.7, r * 0.3);
        c.userData.rest = c.position.y; c.userData.sink = 0.012 - i * 0.004;
        c.renderOrder = 1;
        group.add(c); floaters.push(c);
      });
    } else if (type === 'crushed') {
      // packed to the rim: a rough column, and a mound on top once it's topped up
      const col = new THREE.Mesh(new THREE.CylinderGeometry(inner * 0.94, inner * 0.94, G.h - G.base - 0.004, 16, 6), iceMat);
      const pos = col.geometry.attributes.position;
      for (let i = 0; i < pos.count; i++) { const k = 1 + Math.sin(i * 12.9898) * 0.05; pos.setX(i, pos.getX(i) * k); pos.setZ(i, pos.getZ(i) * k); }
      col.geometry.computeVertexNormals();
      col.position.y = G.base + (G.h - G.base) / 2;
      col.renderOrder = 1;
      group.add(col);
    }
  }
  function mound() {
    const m = new THREE.Mesh(new THREE.IcosahedronGeometry(inner * 1.05, 1).scale(1, 0.55, 1), iceMat);
    m.position.y = G.h;
    m.renderOrder = 1;
    group.add(m);
  }
  function foam(color) {
    const f = new THREE.Mesh(new THREE.CylinderGeometry(inner * 0.99, inner * 0.97, 0.01, 24), pbr({ color, roughness: 0.9, emissive: color, emissiveIntensity: 0.08 }));
    f.position.y = G.base + depth - 0.002;
    group.add(f);
  }
  function frost(k) { // a swizzle frosts the glass over
    glassMat.opacity = 0.13 + k * 0.22; // misted, not white: the drink's colour still shows through
    glassMat.roughness = 0.05 + k * 0.6;
  }
  function garnish(name) {
    const g = garnishFor(name);
    if (!g) return;
    // on the rim or on top, facing you (+z), big enough to read from the stool
    if (name === 'pineapple') { g.position.set(G.r * 0.95, G.h, G.r * 0.3); g.rotation.y = -0.3; } // on the rim to your right, its cut face to you
    else if (name === 'orange') { g.position.set(-G.r * 0.6, G.h - 0.006, G.r * 0.8); g.rotation.set(0, Math.atan2(0.8, 0.6), 0); } // a wheel cut to the middle and set across the rim
    else if (name === 'mint') g.position.set(-0.006, G.h + 0.004, 0.004);
    else if (name === 'beans') g.position.set(0.002, G.base + Math.max(level, 0.5) * depth + 0.003, -0.006); // floating on it
    group.add(g);
  }
  return {
    group, liquid, fill, ice, mound, foam, frost, garnish,
    color(c) { liquidMat.color.set(c); liquidMat.emissive.set(c); },
    get level() { return level; },
    mouth: new THREE.Vector3(0, G.h, 0),
    height: G.h,
  };
}

let citrus = null; // the orange wheel's face, drawn once
/** A garnish, full size (the robot holds one, then it goes on the glass). */
export function garnishFor(name) {
  const g = new THREE.Group();
  if (name === 'pineapple') {
    // a wedge cut from a wheel, slit and sat on the rim: its point in over the drink, the rind
    // outside the glass, and a few of the crown's leaves tucked in behind it standing up. In its
    // own frame +x is out from the glass, the rim at the origin, the cut face toward +z.
    const flesh = pbr({ color: PALETTE.pineapple, roughness: 0.55, emissive: PALETTE.pineapple, emissiveIntensity: 0.15 });
    const rind = pbr({ color: PALETTE.stain, roughness: 0.85 });
    const R = 0.05, half = Math.PI / 7, apex = -0.034; // the wheel's radius, the wedge's half-angle, its point (inside the rim)
    const sector = (r0, r1) => {
      const sh = new THREE.Shape();
      sh.moveTo(Math.cos(-half) * r0, Math.sin(-half) * r0);
      sh.absarc(0, 0, r1, -half, half, false);
      if (r0 > 0) sh.absarc(0, 0, r0, half, -half, true); else sh.lineTo(0, 0);
      return new THREE.ExtrudeGeometry(sh, { depth: 0.014, bevelEnabled: false, curveSegments: 8 }).translate(apex, 0, -0.007);
    };
    g.add(new THREE.Mesh(sector(0, R - 0.006), flesh), new THREE.Mesh(sector(R - 0.006, R), rind));
    const leafM = pbr({ color: PALETTE.leaf, roughness: 0.7, side: THREE.DoubleSide });
    for (let i = 0; i < 3; i++) {
      const leaf = new THREE.Mesh(new THREE.ConeGeometry(0.007, 0.06, 4).translate(0, 0.03, 0).scale(1, 1, 0.3), leafM); // from its foot
      leaf.position.set(apex + R * 0.8, Math.sin(half) * R * 0.4, -0.009);
      leaf.rotation.z = -0.15 - (i - 1) * 0.3;
      g.add(leaf);
    }
  } else if (name === 'orange') {
    // a wheel, cut to the middle and set on the rim, its face to you
    const face = pbr({ map: (citrus = citrus || T.citrusWheel()), roughness: 0.35 });
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.006, 24), [pbr({ color: PALETTE.orange, roughness: 0.6 }), face, face]);
    wheel.rotation.x = Math.PI / 2;
    wheel.position.y = 0.014;
    g.add(wheel);
  } else if (name === 'mint') {
    // a sprig: a stem with leaves in pairs, standing out of the ice
    const m = pbr({ color: PALETTE.mint, roughness: 0.6, side: THREE.DoubleSide });
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.0025, 0.003, 0.06, 5), m);
    stem.position.y = 0.03;
    g.add(stem);
    for (let i = 0; i < 8; i++) {
      const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.016, 8, 6).scale(1, 0.22, 0.55), m);
      const a = Math.floor(i / 2) * 1.6 + (i % 2) * Math.PI, y = 0.02 + Math.floor(i / 2) * 0.012;
      leaf.position.set(Math.cos(a) * 0.012, y, Math.sin(a) * 0.012);
      leaf.rotation.set(0, -a, 0.5);
      g.add(leaf);
    }
  } else if (name === 'beans') {
    const m = pbr({ color: PALETTE.stain, roughness: 0.4 });
    for (let i = 0; i < 3; i++) {
      const b = new THREE.Mesh(new THREE.SphereGeometry(0.009, 10, 8).scale(1, 0.6, 0.75), m);
      b.position.set(Math.cos(i * 2.1) * 0.013, 0, Math.sin(i * 2.1) * 0.013);
      b.rotation.y = i;
      g.add(b);
    }
  } else return null;
  g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
  return g;
}
